import { useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { withEnv } from "@/lib/three";

const ON = new THREE.Color("#F4F4F4");
const OFF = new THREE.Color("#2E2E2E");
const POSTS = [-2.4, 0, 2.4].flatMap((x) => [-1.4, 1.4].map((z) => [x, z]));

const PARTS = (() => {
  const P = [];
  POSTS.forEach(([x, z]) => P.push({ layer: 0, cyl: true, size: [0.26, 0.26, 0.5], pos: [x, 0.25, z] }));
  POSTS.forEach(([x, z]) => P.push({ layer: 1, size: [0.2, 1.2, 0.2], pos: [x, 1.1, z] }));
  [-1.4, 1.4].forEach((z) => P.push({ layer: 2, size: [5.4, 0.26, 0.22], pos: [0, 1.83, z] }));
  for (let x = -2.6; x <= 2.61; x += 0.4) P.push({ layer: 3, size: [0.07, 0.22, 3.3], pos: [x, 2.07, 0] });
  for (let z = -1.6; z <= 1.61; z += 0.23) P.push({ layer: 4, size: [5.7, 0.06, 0.19], pos: [0, 2.22, z] });
  for (let x = -2.8; x <= 2.81; x += 0.7) P.push({ layer: 5, size: [0.07, 0.9, 0.07], pos: [x, 2.7, -1.62] });
  [-2.8, 2.8].forEach((x) => { for (let z = -0.9; z <= 1.2; z += 0.7) P.push({ layer: 5, size: [0.07, 0.9, 0.07], pos: [x, 2.7, z] }); });
  P.push({ layer: 5, size: [5.8, 0.07, 0.12], pos: [0, 3.18, -1.62] });
  [-2.8, 2.8].forEach((x) => P.push({ layer: 5, size: [0.12, 0.07, 3.2], pos: [x, 3.18, 0] }));
  return P;
})();

const Part = ({ part, state }) => {
  const ref = useRef();
  const mat = useRef();
  const line = useRef();
  const geo = useMemo(
    () => (part.cyl ? new THREE.CylinderGeometry(part.size[0], part.size[1], part.size[2], 16) : new THREE.BoxGeometry(...part.size)),
    [part]
  );
  const edges = useMemo(() => new THREE.EdgesGeometry(geo, 30), [geo]);
  useFrame(() => {
    const { explode, active } = state.current;
    ref.current.position.y = part.pos[1] + part.layer * 0.95 * explode;
    const on = active < 0 || active === part.layer;
    mat.current.color.lerp(on ? ON : OFF, 0.1);
    line.current.opacity = THREE.MathUtils.lerp(line.current.opacity, on ? 0.5 : 0.12, 0.1);
  });
  return (
    <group ref={ref} position={part.pos}>
      <mesh geometry={geo} castShadow receiveShadow><meshStandardMaterial ref={mat} color="#F4F4F4" roughness={0.7} /></mesh>
      <lineSegments geometry={edges}><lineBasicMaterial ref={line} color="#0A0A0A" transparent opacity={0.5} /></lineSegments>
    </group>
  );
};

const Rig = ({ children, state }) => {
  const g = useRef();
  const mouse = useRef(0);
  useEffect(() => {
    const f = (e) => { mouse.current = (e.clientX / window.innerWidth) * 2 - 1; };
    window.addEventListener("pointermove", f);
    return () => window.removeEventListener("pointermove", f);
  }, []);
  useFrame(({ clock }) => {
    const ty = -0.7 + mouse.current * 0.3 + Math.sin(clock.elapsedTime * 0.25) * 0.1 + state.current.explode * 0.35;
    g.current.rotation.y = THREE.MathUtils.lerp(g.current.rotation.y, ty, 0.05);
  });
  return <group ref={g}>{children}</group>;
};

export const DeckScene = ({ state, className = "", testId }) => (
  <div className={className} data-testid={testId}>
    <Canvas shadows dpr={[1, 1.75]} camera={{ position: [9.5, 8, 9.5], fov: 36 }} onCreated={(st) => { st.camera.lookAt(1.2, 3.4, -1.2); withEnv(0.3)(st); }}>
      <ambientLight intensity={0.55} />
      <directionalLight position={[6, 12, 4]} intensity={2.2} castShadow shadow-mapSize={[1024, 1024]} shadow-camera-left={-8} shadow-camera-right={8} shadow-camera-top={8} shadow-camera-bottom={-8} />
      <directionalLight position={[-6, 3, -4]} intensity={0.6} />
      <Rig state={state}>
        {PARTS.map((p, i) => <Part key={i} part={p} state={state} />)}
        <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <planeGeometry args={[40, 40]} />
          <shadowMaterial opacity={0.35} />
        </mesh>
        <gridHelper args={[24, 24, "#2A2A2A", "#1A1A1A"]} position={[0, 0.001, 0]} />
      </Rig>
    </Canvas>
  </div>
);

export default DeckScene;
